import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, generatePath, createSearchParams } from 'react-router-dom';

import { JobAvailabilityType, JobType, ParentSignupVerticalTypeId } from 'us-web-services';
import { availabilityTypeConstant } from 'us-react-components/dist/constants/Booking';
import { Confetti } from 'us-react-components/dist/Confetti/Confetti';
import { daysOfWeek } from 'us-react-components/dist/constants/DaysOfWeek';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Storage } from '../../../constants/Storage';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';

export const Success = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    stores: { familyRegistration, user },
    services: { tracker },
  } = useDependencies();

  familyRegistration.setCurrentRoute(Routes.parent.registration.success);

  const getSearchParams = (): URLSearchParams => {
    const { job, jobId, savedSearchId } = familyRegistration;
    const search = new URLSearchParams();

    search.append('fromNux', 'true');

    if (jobId && savedSearchId) {
      search.append('searchId', savedSearchId.toString());
      localStorage.setItem(Storage.SHOW_NUX_SAVED_SEARCH_MODAL, Storage.TRUE);

      const savedSearchData = {
        userId: user.user.id?.toString() || '',
        jobId: jobId.toString(),
        lookingForId: familyRegistration.lookingForId?.toString() || '',
        savedSearchId: familyRegistration.savedSearchId?.toString() || '',
        signupVerticalTypeId: familyRegistration.signupVerticalTypeId?.toString() || '',
        city: familyRegistration.profile?.address?.city?.toString() || '',
        state: familyRegistration.profile?.address?.state?.toString() || '',
        zip: familyRegistration.profile?.address?.zip?.toString() || '',
        street: familyRegistration.profile?.address?.street?.toString() || '',
        latitude: familyRegistration.profile?.address?.latitude?.toString() || '',
        longitude: familyRegistration.profile?.address?.longitude?.toString() || '',
        sunday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[0]?.day)?.weekday?.day?.toString() || '',
        monday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[1]?.day)?.weekday?.day?.toString() || '',
        tuesday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[2]?.day)?.weekday?.day?.toString() || '',
        wednesday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[3]?.day)?.weekday?.day?.toString() || '',
        thursday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[4]?.day)?.weekday?.day?.toString() || '',
        friday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[5]?.day)?.weekday?.day?.toString() || '',
        saturday: familyRegistration.recurring?.days?.find(day => day.weekday?.day === daysOfWeek[6]?.day)?.weekday?.day?.toString() || '',
        availabilityType: familyRegistration.availabilityType?.toString() || '',
        countDependents: familyRegistration.countDependents?.toString() || '',
        timeOfDayId: familyRegistration.recurring?.timeOfDay?.id?.toString() || '',
        parentId: familyRegistration.profile.id?.toString() || '',
        createdOn: familyRegistration.createdOn?.toString() || '',
      };

      localStorage.setItem(Storage.SAVED_SEARCH_DATA, JSON.stringify(savedSearchData));
    } else {
      search.append('city', familyRegistration.profile?.address?.city || '');
      search.append('state', familyRegistration.profile?.address?.state || '');
      search.append('numberOfChildren', (familyRegistration.countDependents ?? 1).toString());

      switch (familyRegistration.signupVerticalTypeId) {
        case ParentSignupVerticalTypeId.PET_CARE_CAT:
          search.append('willSitCat', 'true');
          break;
        case ParentSignupVerticalTypeId.PET_CARE_DOG:
          search.append('willSitDog', 'true');
          break;
        case ParentSignupVerticalTypeId.PET_CARE_OTHER:
          search.append('willSitOtherPet', 'true');
          break;
        case ParentSignupVerticalTypeId.SENIOR_CARE:
          search.append('willSitSenior', 'true');
          break;
        case ParentSignupVerticalTypeId.HOUSEHOLD_SERVICES:
          search.append('willSitHome', 'true');
          break;
        default:
          break; // no additional param for child care vertical; lint just requires a default case
      }

      if (job?.type === JobType.RECURRING) {
        search.append('searchTypeCode', job.availability === JobAvailabilityType.fullTime ? availabilityTypeConstant[2].code : availabilityTypeConstant[1].code);
      } else if (familyRegistration.availabilityType === JobAvailabilityType.occasional) {
        search.append('searchTypeCode', availabilityTypeConstant[3].code);
        if (familyRegistration.startString && familyRegistration.endString) {
          search.append('start', familyRegistration.startString);
          search.append('end', familyRegistration.endString);
        }
      }
    }

    return search;
  };

  const handleContinue = (): void => {
    if (familyRegistration.justBrowsing) {
      familyRegistration.clearData();
      navigate(Routes.parent.dashboard);
    } else {
      const search = createSearchParams(getSearchParams()).toString();

      familyRegistration.clearData();
      navigate({
        pathname: generatePath(Routes.parent.sitters),
        search,
      });
    }
  };

  const getTitle = (): string => familyRegistration.browsingOrAbandoned() ? t('pages.nux.family.success.titleJustBrowsing') : t('pages.nux.family.success.title');
  const getInfo = (): string => familyRegistration.browsingOrAbandoned() ? t('pages.nux.family.success.infoJustBrowsing') : t('pages.nux.family.success.info');
  const getButton = (): string => familyRegistration.browsingOrAbandoned() ? t('pages.nux.family.success.buttonJustBrowsing') : t('pages.nux.family.success.button');

  return (
    <div>
      <Confetti durationSeconds={3} />
      <NuxContainer
        familyRegistration={familyRegistration}
        navigate={navigate}
        tracker={tracker}
        inverted continueTitle={getButton()} onContinue={handleContinue}>
        <div className='flex flex-col w-[90%] md:w-[50%]'>
          <div className='mb-2 font-semibold text-white text-xl text-center md:text-2xl whitespace-pre-line'>
            {getTitle()}
          </div>
          <div className='pt-24 xs:pt-48 font-normal text-white text-base text-center whitespace-pre-line'>
            {getInfo()}
          </div>
        </div>
      </NuxContainer>
    </div>
  );
});
